/**
 * In-memory access-token store shared by an app's Axios and RTK Query clients.
 *
 * The access token lives only in this module-level variable — never in localStorage,
 * sessionStorage, cookies, or persisted Redux state. The refresh token is an HTTP-only
 * cookie the browser sends automatically to the refresh endpoint; JavaScript never sees it.
 *
 * Multi-tab coordination uses BroadcastChannel so a single tab performs each refresh and
 * shares the resulting access token with the other same-origin tabs, avoiding a burst of
 * refresh-token rotations that the server would treat as token reuse.
 */

type AuthConfig = {
  /** API base URL, e.g. '/api'. The refresh endpoint is `${apiBaseUrl}/users/refresh`. */
  apiBaseUrl: string
  /** BroadcastChannel name; must be unique per app so channels don't cross apps. */
  channelName: string
  /**
   * Identifies this app to the backend (e.g. 'manager', 'marketer') so its refresh-token
   * cookie doesn't collide with another app's when both are served from the same origin.
   * Must match the `appId` passed to attachAuthInterceptors for the same app.
   */
  appId?: string
  /** Called when auth is cleared (refresh failed or another tab logged out). */
  onAuthCleared?: () => void
}

let accessToken: string | null = null
let config: AuthConfig = { apiBaseUrl: '/api', channelName: 'adthlete_auth' }
let channel: BroadcastChannel | null = null

// The single in-flight refresh for THIS tab.
let refreshPromise: Promise<string | null> | null = null

// Resolution of a remote wait when the leader tab never answered. Distinct from `null`, which
// means "the leader tried and the session is gone" — a timeout tells us nothing about the
// session, so the waiter must refresh for itself rather than sign the user out.
const REMOTE_TIMEOUT = Symbol('remote-refresh-timeout')
type RemoteResult = string | null | typeof REMOTE_TIMEOUT

// A refresh happening in ANOTHER tab that this tab is waiting on.
let remoteRefresh: {
  promise: Promise<RemoteResult>
  resolve: (token: RemoteResult) => void
  timer: ReturnType<typeof setTimeout>
} | null = null

// If a leader tab dies mid-refresh, don't wait forever.
const REMOTE_REFRESH_TIMEOUT_MS = 10_000

export function configureAuth(opts: Partial<AuthConfig>) {
  config = { ...config, ...opts }
  setupChannel()
}

export function getAccessToken(): string | null {
  return accessToken
}

export function setAccessToken(token: string | null) {
  accessToken = token
}

export function clearAccessToken() {
  accessToken = null
}

function setupChannel() {
  if (typeof BroadcastChannel === 'undefined') return
  if (channel) channel.close()
  channel = new BroadcastChannel(config.channelName)
  channel.onmessage = (event: MessageEvent) => {
    const msg = event.data
    if (!msg || typeof msg !== 'object') return

    switch (msg.type) {
      case 'refresh-start':
        // Another tab is refreshing. Wait for its result instead of starting our own.
        if (!refreshPromise && !remoteRefresh) {
          beginRemoteWait()
        }
        break
      case 'refresh-result':
        accessToken = msg.token ?? null
        resolveRemoteWait(accessToken)
        // The refresh cookie is per-origin, so a leader tab that failed to refresh means the
        // session is gone for THIS tab too. Clear user state here as well, otherwise this tab
        // keeps rendering a signed-in UI while every request goes out unauthenticated.
        if (!accessToken) {
          config.onAuthCleared?.()
        }
        break
      case 'token':
        accessToken = msg.token ?? null
        if (!accessToken) {
          config.onAuthCleared?.()
        }
        break
      case 'logout':
        accessToken = null
        config.onAuthCleared?.()
        break
    }
  }
}

function broadcast(msg: Record<string, unknown>) {
  channel?.postMessage(msg)
}

function beginRemoteWait() {
  let resolve!: (token: RemoteResult) => void
  const promise = new Promise<RemoteResult>((res) => {
    resolve = res
  })
  // Never fall back to the token we already hold — it is the expired one that started this
  // refresh, and handing it back would make callers retry with a JWT the server rejects.
  const timer = setTimeout(() => resolveRemoteWait(REMOTE_TIMEOUT), REMOTE_REFRESH_TIMEOUT_MS)
  remoteRefresh = { promise, resolve, timer }
}

function resolveRemoteWait(token: RemoteResult) {
  if (!remoteRefresh) return
  clearTimeout(remoteRefresh.timer)
  remoteRefresh.resolve(token)
  remoteRefresh = null
}

/**
 * Broadcast a logout to other tabs and clear this tab's token.
 */
export function broadcastLogout() {
  accessToken = null
  broadcast({ type: 'logout' })
}

/**
 * Obtain a fresh access token by rotating the refresh session. Coordinated so that only one
 * refresh runs per tab, and preferably only one across all same-origin tabs. Uses a bare
 * fetch with credentials so it never carries a stale bearer token or triggers retry logic.
 *
 * @returns the new access token, or null if the session could not be refreshed.
 */
export async function refreshAccessToken(): Promise<string | null> {
  if (refreshPromise) return refreshPromise

  if (remoteRefresh) {
    const result = await remoteRefresh.promise
    // A real answer from the leader tab — a token, or null meaning the session is gone (that
    // path already cleared auth state). Only a timeout falls through to our own attempt.
    if (result !== REMOTE_TIMEOUT) return result
    if (refreshPromise) return refreshPromise
  }

  broadcast({ type: 'refresh-start' })

  refreshPromise = (async () => {
    let token: string | null = null
    try {
      const res = await fetch(`${config.apiBaseUrl}/users/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(config.appId ? { 'X-App-Id': config.appId } : {}),
        },
      })
      if (res.ok) {
        const data = await res.json().catch(() => ({}))
        token = data.token ?? null
      }
    } catch {
      token = null
    }

    accessToken = token
    broadcast({ type: 'refresh-result', token })
    // A failed refresh means the session is gone; let the app clear user state so route
    // guards redirect to sign-in.
    if (!token) {
      config.onAuthCleared?.()
    }
    return token
  })()

  try {
    return await refreshPromise
  } finally {
    refreshPromise = null
  }
}
